<template>
    <div class="deployment-field" :class="{ 'mb-2': field.type != 'hidden' }">
        <label v-if="shouldShowLabel" class="form-label">
            <span v-html="field.label"></span><em v-if="!field.required" class="text-muted small"> (Optional)</em>
            <img v-if="field.tooltip && field.tooltip.length > 0" class="info-icon" src="/img/icons/avid-icons/info.svg" data-bs-toggle="tooltip" :title="field.tooltip"></img>
        </label>

        <input v-if="field.type == 'text'" type="text" class="form-control"
            :name="field.id" v-model="field.value" :id="field.id"
            :class="field.error || !isValidRegex ? 'is-invalid' : ''"
            :disabled="isDisabled" @change="validateField" aria-label="Field.id">

        <input v-if="field.type == 'label'" type="text"
            class="form-control" disabled :id="field.id"
            :name="field.id" v-model="field.value" aria-label="Field.id">

        <input v-if="field.type == 'hidden'" type="hidden"
            :id="field.id" :name="field.id" v-model="field.value">

        <textarea v-if="field.type == 'textarea'" :name="field.id"
            class="form-control full-height" v-model="field.value" :id="field.id"
            :class="field.error || !isValidRegex ? 'is-invalid' : ''"
            :disabled="isDisabled" @change="validateField"></textarea>

        <div v-if="field.type == 'password' || field.type == 'password-text'" class="input-group">
            <input :type="field.type == 'password' ? 'password' : 'text'"
                class="form-control password-input" :id="field.id"
                :name="field.id" v-model="field.value"
                :class="field.error || !isValidRegex ? 'is-invalid' : ''"
                :disabled="isDisabled" @change="validateField" aria-label="Field.id">
            <span class="input-group-text border-none" @click="toggleVisibility">
                <i class="fa" :class="[!field.visible ? 'fa-eye' : 'fa-eye-slash']"></i>
            </span>
        </div>

        <div v-if="field.type == 'select' && field.options && Object.entries(field.options).length > 0" class="select-wrapper">
            <div v-if="field.searchable" class="searchable-select position-relative">
                <button class="btn btn-field dropdown-toggle w-100 text-start select-field"
                    :disabled="loadingDependentData && field.depends_on || isDisabled"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false">
                    <span class="text-truncate d-inline-block" style="max-width: 100%;">{{ loadingDependentData && field.depends_on ? 'Loading...' : (field.value ? field.options[field.value] : 'Select...' ) }}</span>
                </button>
                <div class="dropdown-menu dropdown-menu-primary w-100">
                    <div class="position-relative search-container" v-if="Object.entries(field.options).length > 10">
                        <input type="text"
                            class="search-box form-control mb-2 py-1"
                            placeholder="Search..."
                            v-model="field.searchQuery"
                            @input="filterOptions" aria-label="Search...">
                        <i class="fa fa-search search-placeholder-icon" v-if="!field.searchQuery"></i>
                    </div>
                    <div class="options-container">
                        <li v-for="option in filteredOptions()"
                            :key="option.id"
                            class="dropdown-item py-1 px-2">
                            <hr v-if="option.id == 'divider'" class="dropdown-divider">
                            <a v-else
                                href="#" class="text-decoration-none d-block w-100 py-1"
                                @click.prevent="selectOption(option.id)">
                                {{ option.name }}
                            </a>
                        </li>
                    </div>
                </div>
            </div>
            <select v-else class="form-select" :name="field.id" v-model="field.value"
                @change="handleSelectChange"
                :disabled="isDisabled" aria-label="Field.id">
                <option v-for="(option, key) in selectableOptions" :key="key" :value="key">
                    {{ option }}
                </option>
            </select>
            <input type="hidden" :id="field.id" :name="field.id" v-model="field.value">
            <!-- Notice below dropdown -->
            <div v-if="fieldNotice" class="mt-2">
                <div :class="['alert', 'alert-' + fieldNotice.type]">
                    <p class="mb-0">{{ fieldNotice.message }}</p>
                    <a v-if="fieldNotice.link" :href="fieldNotice.link" target="_blank" class="alert-link">Learn more</a>
                </div>
            </div>
        </div>

        <div v-if="field.type == 'multiselect' && field.options && Object.entries(field.options).length > 0" class="select-wrapper">
            <div class="searchable-select position-relative">
                <button class="btn btn-field dropdown-toggle w-100 text-start select-field"
                    :disabled="loadingDependentData && field.depends_on || isDisabled"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false">
                    <span class="text-truncate d-inline-block" style="max-width: 100%;">{{ loadingDependentData && field.depends_on ? 'Loading...' : (field.value && field.value.length > 0 ? (field.value.length === 1 ? field.options[field.value[0]] : field.value.length + ' selected') : 'Select...') }}</span>
                </button>
                <div class="dropdown-menu dropdown-menu-primary w-100">
                    <div class="position-relative search-container" v-if="Object.entries(field.options).length > 10">
                        <input type="text"
                            class="search-box form-control mb-2 py-1"
                            placeholder="Search..."
                            v-model="field.searchQuery"
                            @input="filterOptions" aria-label="Search...">
                        <i class="fa fa-search search-placeholder-icon" v-if="!field.searchQuery"></i>
                    </div>
                    <div class="options-container">
                        <li v-for="option in filteredOptions()"
                            :key="option.id"
                            class="dropdown-item py-1 px-2">
                            <hr v-if="option.id == 'divider'" class="dropdown-divider">
                            <div v-else
                                class="dropdown-item py-1 px-2 d-flex align-items-center"
                                @click="toggleMultiselectOption(option.id)"
                                @click.stop>
                                <input type="checkbox"
                                :id="'multiselect_option_' + option.id"
                                class="form-check-input me-2 mt-0"
                                :checked="field.value && field.value.includes(option.id)"
                                @change="toggleMultiselectOption(option.id)"
                                @click.stop aria-label="Checkbox">
                                <span class="flex-grow-1">{{ option.name }}</span>
                            </div>
                        </li>
                    </div>
                </div>
            </div>
            <input type="hidden" :id="field.id" :name="field.id" v-model="field.value">
        </div>

        <div v-if="field.type == 'select-other'">
            <div class="input-group deployment-field-input-group" style="min-width: 0;">
                <button class="btn btn-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false"
                    style="width: 100px;" :disabled="!hasOptions">
                    {{ !hasOptions || field.visible == true ? 'Add New' : 'Options' }}
                </button>
                <ul class="dropdown-menu dropdown-menu-primary">
                    <li v-if="hasOptions">
                        <a class="dropdown-item" href="#" @click.prevent="toggleSelectOther(); field.visible = false" :class="{ 'active': field.visible == false }">
                            Options
                        </a>
                    </li>
                    <li>
                        <a class="dropdown-item" href="#" @click.prevent="toggleSelectOther(); field.visible = true" :class="{ 'active': field.visible == true }">
                            Add New
                        </a>
                    </li>
                </ul>

                <div v-if="hasOptions && !field.visible && field.searchable" class="searchable-select flex-grow-1 position-relative" style="min-width: 0; max-width: calc(100% - 100px);">
                    <button class="btn btn-field dropdown-toggle w-100 text-start select-field"
                        :disabled="loadingDependentData && field.depends_on || isDisabled"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false">
                        <span class="text-truncate d-inline-block" style="max-width: 100%;">{{ loadingDependentData && field.depends_on ? 'Loading...' : (field.value ? field.options[field.value] : 'Select...' ) }}</span>
                    </button>
                    <div class="dropdown-menu dropdown-menu-primary w-100">
                        <div class="position-relative search-container" v-if="Object.entries(field.options).length > 10">
                            <input type="text"
                                class="search-box form-control mb-2 py-1"
                                placeholder="Search..."
                                v-model="field.searchQuery"
                                @input="filterOptions" aria-label="Search...">
                            <i class="fa fa-search search-placeholder-icon" v-if="!field.searchQuery"></i>
                        </div>
                        <div class="options-container" :class="{'pt-0': Object.entries(field.options).length <= 10}">
                            <li v-for="option in filteredOptions()"
                                :key="option.id"
                                class="dropdown-item py-1 px-2">
                                <hr v-if="option.id == 'divider'" class="dropdown-divider">
                                <a v-else
                                    href="#" class="text-decoration-none d-block w-100"
                                    @click.prevent="selectOption(option.id)">
                                    {{ option.name }}
                                </a>
                            </li>
                        </div>
                    </div>
                </div>
                <input v-else type="text" class="form-control" v-model="field.value" :id="field.id"
                    :class="field.error || !isValidRegex ? 'is-invalid' : ''"
                    :disabled="isDisabled" @change="validateField" aria-label="Value">
            </div>
            <input type="hidden" :id="field.id" :name="field.id" v-model="field.value">
            <!-- Notice below select-other -->
            <div v-if="fieldNotice" class="mt-2">
                <div :class="['alert', 'alert-' + fieldNotice.type]">
                    <p class="mb-0">{{ fieldNotice.message }}</p>
                    <a v-if="fieldNotice.link" :href="fieldNotice.link" target="_blank" class="alert-link">Learn more</a>
                </div>
            </div>
        </div>

        <!-- Hierarchical folder tree picker for Marketo -->
        <div v-if="field.type == 'folder-tree' && field.tree_options" class="select-wrapper">
            <div class="searchable-select position-relative">
                <button class="btn btn-field dropdown-toggle w-100 text-start select-field"
                    type="button"
                    data-bs-toggle="dropdown"
                    data-bs-auto-close="outside"
                    aria-expanded="false"
                    :disabled="isDisabled">
                    <span class="text-truncate d-inline-block" style="max-width: 100%;">
                        {{ selectedFolderName || 'Select folder...' }}
                    </span>
                </button>
                <div class="dropdown-menu dropdown-menu-primary w-100 p-0">
                    <div class="folder-tree-search px-2 pt-2 pb-1 border-bottom">
                        <div class="folder-tree-search-input-wrap position-relative">
                            <input type="text"
                                class="search-box form-control py-1"
                                placeholder="Search folders..."
                                v-model="folderSearchQuery"
                                @click.stop>
                            <i class="fa fa-search folder-tree-search-icon" v-if="!folderSearchQuery" aria-hidden="true"></i>
                        </div>
                    </div>
                    <div class="options-container folder-tree-list" style="max-height: 260px; overflow-y: auto;">
                        <div v-for="node in visibleTreeNodes" :key="node.id"
                            class="folder-tree-node d-flex align-items-center py-1"
                            :style="{ paddingLeft: (node.depth * 14 + 8) + 'px', paddingRight: '8px' }"
                            :class="{ 'folder-tree-node--selected': String(field.value) === String(node.id) }">
                            <button v-if="node.hasChildren"
                                class="btn p-0 border-0 bg-transparent me-1 folder-tree-toggle"
                                style="width: 16px; flex-shrink: 0;"
                                type="button"
                                @click.stop="toggleFolderNode(node.id)">
                                <i class="fa text-muted" :class="expandedFolderIds[node.id] ? 'fa-chevron-down' : 'fa-chevron-right'" style="font-size: 10px;"></i>
                            </button>
                            <span v-else style="width: 16px; flex-shrink: 0;" class="me-1"></span>
                            <i class="fa fa-folder me-2 folder-tree-icon" style="font-size: 12px; flex-shrink: 0;"></i>
                            <a href="#"
                                class="text-decoration-none text-body flex-grow-1 small py-1"
                                style="min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                                @click.prevent="selectFolderOption(node.id)">
                                {{ node.name }}
                            </a>
                        </div>
                        <div v-if="visibleTreeNodes.length === 0" class="text-muted small px-3 py-2">
                            No folders found
                        </div>
                    </div>
                </div>
            </div>
            <input type="hidden" :id="field.id" :name="field.id" v-model="field.value">
        </div>

        <div v-if="field.type == 'checkbox'" class="form-check">
            <input class="form-check-input" type="checkbox" :id="field.id"
                :name="field.id" v-model="field.value"
                :disabled="isDisabled" @change="validateField" aria-label="Field.id">
            <label class="form-check-label" :for="field.id">
                {{ field.label }}
            </label>
        </div>

        <div v-if="field.type == 'radio' && field.options" class="radio-group">
            <div v-for="(optionLabel, optionKey) in field.options" :key="optionKey" class="form-check">
                <input class="form-check-input" type="radio" 
                    :id="field.id + '_' + optionKey"
                    :name="field.id" 
                    :value="optionKey"
                    v-model="field.value"
                    :disabled="isDisabled" 
                    @change="validateField" aria-label="Field.id">
                <label class="form-check-label" :for="field.id + '_' + optionKey">
                    {{ optionLabel }}
                </label>
            </div>
        </div>

        <datepicker-utc v-if="field.type == 'date'"
            v-model="field.value" placeholder="" 
            input-class="form-control date" 
            @update:modelValue="validateField">
        </datepicker-utc>

        <div class="text-danger invalid-feedback" v-if="field.error" v-html="field.error_msg">
        </div>
        <div class="text-danger invalid-feedback" v-if="!isValidRegex && regexErrorMessage">
            {{ regexErrorMessage }}
        </div>
    </div>
</template>

<script>
export default {
    name: 'DeploymentField',
    props: {
        field: {
            type: Object,
            required: true
        },
        loadingDependentData: {
            type: Boolean,
            default: false
        },
        isDisabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isValidRegex: true, // Start as valid, will be updated on change
            expandedFolderIds: {},
            folderSearchQuery: ''
        }
    },
    computed: {
        hasOptions() {
            return this.field.options && Object.keys(this.field.options).length > 0
        },
        shouldShowLabel() {
            // Allow explicit override to show/hide label
            if (this.field.show_label !== undefined) {
                return this.field.show_label
            }

            // Hide label for checkbox and hidden fields
            if (this.field.type === 'checkbox' || this.field.type === 'hidden') {
                return false
            }

            // Hide label for select, multiselect, or select-other fields with no options
            if (['select', 'multiselect', 'select-other'].includes(this.field.type) && !this.hasOptions) {
                return false
            }

            return true
        },
        regexErrorMessage() {
            if (!this.field.regex || this.isValidRegex) {
                return ''
            }
            
            const label = this.field.label || 'field'
            return `Please enter a valid ${label.toLowerCase()}`
        },
        fieldNotice() {
            // Extract notice from options if it exists
            if (this.field.options && this.field.options.notice && typeof this.field.options.notice === 'object') {
                return this.field.options.notice
            }
            return null
        },
        selectedFolderName() {
            if (!this.field.value || !this.field.tree_options) return null
            const found = this.field.tree_options.find(o => String(o.id) === String(this.field.value))
            return found ? found.name : null
        },
        treeNodes() {
            if (!this.field.tree_options || !Array.isArray(this.field.tree_options)) return []
            const items = this.field.tree_options

            // Build parent→children map for hasChildren flag
            const childrenMap = {}
            items.forEach(item => { childrenMap[item.id] = [] })
            items.forEach(item => {
                if (item.parentId && childrenMap[item.parentId]) {
                    childrenMap[item.parentId].push(item.id)
                }
            })

            const nodes = items.map(item => {
                const depth = item.path ? Math.max(0, item.path.split('/').filter(Boolean).length - 1) : 0
                return { ...item, depth, hasChildren: (childrenMap[item.id] || []).length > 0 }
            })

            nodes.sort((a, b) => (a.path || '').localeCompare(b.path || ''))
            return nodes
        },
        visibleTreeNodes() {
            const nodes = this.treeNodes
            const query = this.folderSearchQuery.toLowerCase().trim()

            if (query) {
                return nodes.filter(n =>
                    n.name.toLowerCase().includes(query) ||
                    (n.path && n.path.toLowerCase().includes(query))
                )
            }

            // Build path→node map for O(1) ancestor lookups
            const pathMap = {}
            nodes.forEach(n => { pathMap[n.path] = n })

            return nodes.filter(node => {
                if (node.depth === 0) return true
                const parts = node.path.split('/').filter(Boolean)
                for (let i = 1; i < parts.length; i++) {
                    const ancestorPath = '/' + parts.slice(0, i).join('/')
                    const ancestor = pathMap[ancestorPath]
                    if (!ancestor || !this.expandedFolderIds[ancestor.id]) return false
                }
                return true
            })
        },
        selectableOptions() {
            // Filter out notice and divider from options for regular select
            if (!this.field.options) return {}
            const filtered = {}
            for (const [key, value] of Object.entries(this.field.options)) {
                if (key !== 'notice' && key !== 'divider') {
                    filtered[key] = value
                }
            }
            return filtered
        }
    },
    watch: {
        field: {
            handler(newField) {
                // Reset search query when field changes
                if (newField.searchable && !newField.searchQuery) {
                    newField.searchQuery = '';
                }
            },
            deep: true
        },
    },
    mounted() {
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]')
        const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => new window.bootstrap.Tooltip(tooltipTriggerEl))

        for (let i = 0; i < tooltipTriggerList.length; i++) {
            (function(index) {
                tooltipTriggerList[index].addEventListener('click', function() {
                    // Hide the tooltip using the index passed to the closure
                    tooltipList[index].hide()
                })
                tooltipTriggerList[index].addEventListener('mouseleave', function() {
                    // Hide the tooltip using the index passed to the closure
                    tooltipList[index].hide()
                })
            })(i)
        }

        // Set default state for select-other fields with no options
        if (this.field.type === 'select-other' && !this.hasOptions) {
            this.field.visible = true
        }
        // Set default state for multiselect fields while preserving backend defaults
        if (this.field.type == 'multiselect' && !Array.isArray(this.field.value)) {
            this.field.value = [];
        }
        
        // Initialize validation state
        this.validateField();
    },
    methods: {
        toggleFolderNode(id) {
            this.expandedFolderIds = { ...this.expandedFolderIds, [id]: !this.expandedFolderIds[id] }
        },
        selectFolderOption(id) {
            this.field.value = id
            // Close the Bootstrap dropdown
            const btn = this.$el.querySelector('[data-bs-toggle="dropdown"]')
            if (btn && window.bootstrap) {
                const dd = window.bootstrap.Dropdown.getInstance(btn)
                if (dd) dd.hide()
            }
            this.handleSelectChange()
        },
        toggleVisibility() {
            if (this.field.type === 'password') {
                this.field.type = 'password-text'
                this.field.visible = true
            } else if (this.field.type === 'password-text') {
                this.field.type = 'password'
                this.field.visible = false
            }
        },
        handleSelectChange() {
            this.$emit('select-change', this.field)
            this.$emit('change')
        },
        toggleSelectOther() {
            // If there are no options, always stay in "Add New" mode
            if (!this.hasOptions) {
                this.field.visible = true
                return
            }

            if(this.field.visible) {
                //See if the value is in the list of options. If not, clear it.
                if(!Object.values(this.field.options).includes(this.field.value)) {
                    this.field.value = ''
                }
            }
            this.field.visible = !this.field.visible
            this.$forceUpdate()
            
            // Emit change event when switching modes
            this.$emit('change')
        },
        filterOptions() {
            if (!this.field.searchable) {
                // Filter out notice and divider from options
                const filtered = {}
                for (const [key, value] of Object.entries(this.field.options)) {
                    if (key !== 'notice' && key !== 'divider' && typeof value === 'string') {
                        filtered[key] = value
                    }
                }
                return filtered
            }

            const query = this.field.searchQuery?.toLowerCase() || ''
            return Object.entries(this.field.options).reduce((filtered, [key, value]) => {
                // Skip notice and divider keys, and only include string values
                if (key !== 'notice' && key !== 'divider' && typeof value === 'string' && value.toLowerCase().includes(query)) {
                    filtered[key] = value
                }
                return filtered
            }, {})
        },
        filteredOptions() {
            if (!this.field.searchable) {
                const options = []
                for (const [key, value] of Object.entries(this.field.options)) {
                    // Skip notice and divider keys
                    if (key !== 'notice' && key !== 'divider' && typeof value === 'string') {
                        options.push({ id: key, name: value })
                    }
                }
                if (this.field.type === 'multiselect') {
                    const withSelectedFirst = this.multiselectOptionsWithSelectedFirst(options)
                    if (withSelectedFirst !== null) {
                        return withSelectedFirst
                    }
                }
                return options
            }
            const filtered = this.filterOptions()
            let arr = []
            for (const [key, value] of Object.entries(filtered)) {
                arr.push({ id: key, name: value })
            }

            if (this.field.type === 'multiselect') {
                const withSelectedFirst = this.multiselectOptionsWithSelectedFirst(arr)
                if (withSelectedFirst !== null) {
                    return withSelectedFirst
                }
            }

            return this.sortOptionsAvidAndAlpha(arr)
        },
        /**
         * Multiselect: selected ids first (order matches field.value), then the rest with AVID grouping.
         * @param {Array<{id: string, name: string}>} items
         * @returns {Array<{id: string, name: string}>|null} null if nothing selected (use default list sort)
         */
        multiselectOptionsWithSelectedFirst(items) {
            if (!Array.isArray(this.field.value) || this.field.value.length === 0) {
                return null
            }
            const selectedSet = new Set(this.field.value.map(v => String(v)))
            const selected = []
            const unselected = []
            for (const item of items) {
                if (selectedSet.has(String(item.id))) {
                    selected.push(item)
                } else {
                    unselected.push(item)
                }
            }
            const orderMap = new Map(this.field.value.map((v, i) => [String(v), i]))
            selected.sort((a, b) => (orderMap.get(String(a.id)) ?? 0) - (orderMap.get(String(b.id)) ?? 0))
            return [...selected, ...this.sortOptionsAvidAndAlpha(unselected)]
        },
        sortOptionsAvidAndAlpha(arr) {
            const avidItems = arr.filter(item => item.name.startsWith('AVID -'))
            const otherItems = arr.filter(item => !item.name.startsWith('AVID -'))
            avidItems.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
            otherItems.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
            if (avidItems.length > 0 && otherItems.length > 0) {
                return [...avidItems, { id: 'divider', name: 'hr' }, ...otherItems]
            }
            return [...avidItems, ...otherItems]
        },
        selectOption(value) {
            if (this.field.type === 'multiselect') {
                if (!this.field.value) {
                    this.field.value = []
                }
                const index = this.field.value.indexOf(value)
                if (index === -1) {
                    this.field.value.push(value)
                } else {
                    this.field.value.splice(index, 1)
                }
                // Emit change event for multiselect
                this.$emit('change')
            } else {
                this.field.value = value
                this.handleSelectChange()
            }
        },
        toggleMultiselectOption(value) {
            this.selectOption(value)
        },
        validateField() {
            // Trim string values (e.g. URLs) so spaces around the value don't fail validation
            if (typeof this.field.value === 'string' && this.field.regex) {
                const trimmed = this.field.value.trim();
                if (trimmed !== this.field.value) {
                    this.field.value = trimmed;
                }
            }

            // Perform regex validation only when field value changes
            if (!this.field.regex) {
                this.isValidRegex = true;
            } else {
                // If field has no value, consider it valid (required validation is handled elsewhere)
                const valueToTest = typeof this.field.value === 'string' ? this.field.value.trim() : this.field.value;
                if (!valueToTest || valueToTest === '') {
                    this.isValidRegex = true;
                } else {
                    try {
                        const regex = new RegExp(this.field.regex);
                        this.isValidRegex = regex.test(valueToTest);
                    } catch (error) {
                        // If regex is invalid, consider field valid to avoid breaking the form
                        console.warn('Invalid regex pattern:', this.field.regex);
                        this.isValidRegex = true;
                    }
                }
            }

            // Emit change event to notify parent component
            this.$emit('change');
        }
    }
}
</script>